import { useTranslation } from "react-i18next";

import { formatCount } from "@/lib/format";
import { tp } from "@/lib/plural";

import type { ActId } from "../acts";
import { DECISION_BAR } from "../acts/environment/WricefCard";
import { makeDecisionMeta } from "../diagnosticMeta";
import { StackedBar } from "../primitives/StackedBar";
import { NO_VALUE } from "../sectionState";
import { SummaryBlock, SummaryStat } from "./SummaryBlock";
import { SummaryLegend, Swatch } from "./SummaryLegend";
import type { DiagnosticSummary } from "./summaryData";

/**
 * Bloco 3 · O tratamento (EF-DIAG-004 RF-05): o que acontece com os
 * desenvolvimentos próprios, pela decisão do arquiteto.
 *
 * Sem decisão registrada o universo continua conhecido, mas quantos convertem e
 * quantos saem não: aparecem como aguardando, e a barra não é desenhada.
 */
export function TreatmentBlock({
  summary,
  act,
  onGoToAct,
}: {
  summary: DiagnosticSummary["treatment"];
  act: ActId;
  onGoToAct: (act: ActId) => void;
}) {
  const { t } = useTranslation();
  const labels = makeDecisionMeta(t);
  const decided = summary.state === "decided" ? summary : undefined;
  const converted = decided?.byDecision.find((entry) => entry.decision === "in_scope");

  function reading(): string | undefined {
    if (!decided || converted?.pct === undefined) return undefined;
    return tp(
      t,
      decided.analyzed,
      "Of {{count}} development, {{converted}} ({{pct}}%) go on to conversion.",
      "Of {{count}} developments, {{converted}} ({{pct}}%) go on to conversion.",
      { converted: formatCount(decided.inConversion), pct: converted.pct },
    );
  }

  return (
    <SummaryBlock
      id="treatment"
      index={3}
      overline={t("The treatment")}
      title={t("What we do with the custom code")}
      act={act}
      onGoToAct={onGoToAct}
      reading={reading()}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <SummaryStat
          testId="summary-analyzed"
          value={summary.analyzed === undefined ? NO_VALUE : formatCount(summary.analyzed)}
          label={t("Developments analyzed (WRICEF)")}
        />
        <SummaryStat
          testId="summary-converted"
          value={decided ? formatCount(decided.inConversion) : NO_VALUE}
          label={t("Will be converted")}
        />
        <SummaryStat
          testId="summary-leaving"
          value={decided ? formatCount(decided.leavingOrStandard) : NO_VALUE}
          label={t("Leave or become standard")}
        />
      </div>

      {decided ? (
        <div className="flex flex-col gap-2">
          <StackedBar
            height="h-3.5"
            segments={decided.byDecision.map((entry) => ({
              key: entry.decision,
              label: labels[entry.decision].label,
              className: DECISION_BAR[entry.decision],
              value: entry.value,
            }))}
          />
          <SummaryLegend
            testIdPrefix="summary-decision"
            items={decided.byDecision.map((entry) => ({
              key: entry.decision,
              label: labels[entry.decision].label,
              value: formatCount(entry.value),
              share: entry.pct === undefined ? undefined : `${entry.pct}%`,
              swatch: <Swatch className={DECISION_BAR[entry.decision]} />,
            }))}
          />
        </div>
      ) : (
        <p className="m-0 text-[11.5px] leading-snug text-[#7b8799]">
          {t("Awaiting the architect's decision on the developments.")}
        </p>
      )}
    </SummaryBlock>
  );
}
